import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AdminEventJourneyDto, AdminEventJourneyUserDto, ApiError } from '@ivy-monorepo/shared'
import { adminService } from '../../services/admin.service'
import Pagination from '../../components/Pagination'
import EventJourneyTimeline from '../../components/EventJourneyTimeline'
import EventJourneySummary from '../../components/EventJourneySummary'
import { formatEventSource, formatJourneyDate } from '../../utils/eventJourney'

type UserFilter = 'converted' | 'all'

const PAGE_SIZE = 20

/**
 * How users reached one event: funnel totals, RSVPs / tickets per source, and every user's steps
 * (hotspot modal, CTA, "Get a Ticket", RSVP, ticket purchase) in time order.
 */
const EventJourneyPage = () => {
  const { publicId } = useParams<{ publicId: string }>()
  const [summary, setSummary] = useState<AdminEventJourneyDto | null>(null)
  const [error, setError] = useState('')
  const [users, setUsers] = useState<AdminEventJourneyUserDto[]>([])
  const [userFilter, setUserFilter] = useState<UserFilter>('converted')
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState({ total: 0, totalPages: 0 })
  const [usersLoading, setUsersLoading] = useState(false)

  useEffect(() => {
    if (!publicId) return
    adminService
      .getEventJourney(publicId)
      .then(setSummary)
      .catch((err: ApiError) => setError(err.friendlyMessage || err.message || 'Failed to load the event journey.'))
  }, [publicId])

  useEffect(() => {
    if (!publicId) return
    setUsersLoading(true)
    adminService
      .getEventJourneyUsers(publicId, page, PAGE_SIZE, userFilter)
      .then(response => {
        setUsers(response.users)
        setPagination({ total: response.pagination.total, totalPages: response.pagination.totalPages })
      })
      .catch((err: ApiError) => setError(err.friendlyMessage || err.message || 'Failed to load user journeys.'))
      .finally(() => setUsersLoading(false))
  }, [publicId, page, userFilter])

  const handleFilterChange = (filter: UserFilter) => {
    setUserFilter(filter)
    setPage(1)
  }

  if (error && !summary) {
    return <div className="error-message">{error}</div>
  }
  if (!summary) {
    return <></>
  }

  return (
    <div className="roles-container event-journey-page">
      <div className="roles-header">
        <h2>
          Event journey · {summary.event.name}
          <span className="event-journey-page__venue"> at {summary.venue.name}</span>
        </h2>
        <Link to="/admin/venue-events" className="styled-button sm">
          <span>Back to events</span>
        </Link>
      </div>
      <p className="roles-page-intro">
        How users reached this event. Counts include every occurrence; &quot;users&quot; are unique people. RSVP and ticket sources are where the user opened
        the event from; &quot;Not reported&quot; means an app build released before source tracking.
      </p>

      {error && <div className="error-message">{error}</div>}

      <EventJourneySummary summary={summary} />

      <div className="event-journey-users-header">
        <h3 className="event-journey-page__heading">User journeys ({pagination.total.toLocaleString()})</h3>
        <div className="tab-bar">
          <button type="button" className={userFilter === 'converted' ? 'tab active' : 'tab'} onClick={() => handleFilterChange('converted')}>
            RSVP&apos;d or bought
          </button>
          <button type="button" className={userFilter === 'all' ? 'tab active' : 'tab'} onClick={() => handleFilterChange('all')}>
            Everyone with a step
          </button>
        </div>
        {pagination.totalPages > 1 && <Pagination currentPage={page} totalPages={pagination.totalPages} onPageChange={setPage} />}
      </div>

      {!usersLoading && users.length === 0 && <p className="event-journey-empty">No users yet.</p>}
      <div className="event-journey-users">
        {users.map(journey => (
          <section key={journey.user.publicId} className="event-journey-user">
            <div className="event-journey-user__header">
              <Link to={`/admin/users/${journey.user.publicId}`} className="event-journey-user__name">
                {journey.user.firstName} {journey.user.lastName}
              </Link>
              {journey.user.telephone && <span className="event-journey-user__meta">{journey.user.telephone}</span>}
              <span className="event-journey-user__meta">
                RSVP: {journey.rsvpedAt ? `${formatJourneyDate(journey.rsvpedAt)} from ${formatEventSource(journey.rsvpSource)}` : '—'}
              </span>
              <span className="event-journey-user__meta">
                Ticket: {journey.ticketStatus ? `${journey.ticketStatus} from ${formatEventSource(journey.ticketSource)}` : '—'}
              </span>
            </div>
            <EventJourneyTimeline steps={journey.steps} />
          </section>
        ))}
      </div>
    </div>
  )
}

export default EventJourneyPage
